import React, { useState, useCallback } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import dateHandler from "@/composables/dateUtil";
import FilePreviewModal from "@/components/common/FilePreviewModal";
import { useNavigate, useParams } from "react-router-dom";
import { EndpointType } from "@/constants/api";
import { DocumentType } from "../../DocumentView/components/DocumentDetails";
import FileIcon from "@styles/all-images/icons/file.svg";

const IMDocumentHistoryTable = () => {
  const { id, type } = useParams();
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDateWithTime } = dateHandler();
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const navigate = useNavigate();

  const handleView = useCallback((doc: any, type: DocumentType) => {
    navigate(`/view-im-ts-document/${doc.id}?type=${type}&history=true`);
  }, []);

  const columns = [
    {
      header: "Document Name",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#F4EBFF]">
            <img
              src={FileIcon}
              alt={"File Icon"}
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-5 tw-max-h-5"
            />
          </span>
          <span className="tw-truncate tw-max-w-[300px]">
            {row.document_title || NO_DATA_AVAILABLE}
          </span>
        </div>
      ),
      sortable: true,
      sortKey: "document_title",
    },
    {
      header: "Uploaded on",
      accessor: (row: any) => formatDateWithTime(row.action_at),
      sortable: true,
      sortKey: "action_at",
    },
    {
      header: "Action",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            onClick={() => handleView(row, type as DocumentType)}
            title="View"
          >
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>
        </div>
      ),
    },
  ];

  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const mappingEndpointWithDocType: Record<
          string | DocumentType,
          EndpointType
        > = {
          [DocumentType.IM]: "imDocumentHistoryList",
          [DocumentType.TS]: "tsDocumentHistoryList",
        };

        const { getAllData } = actionsHandler(
          mappingEndpointWithDocType[type as string | DocumentType],
        );

        let ordering = "";
        if (params.sortColumn) {
          ordering =
            params.sortDirection === "desc"
              ? `-${params.sortColumn}`
              : params.sortColumn;
        }
        const response = await dispatch(
          getAllData({
            method: "get",
            customParams: id ? [id + "/"] : undefined,
            limit: params.limit || 10,
            page: params.page || 1,
            search: params.search || "",
            customQuery: ordering ? `ordering=${ordering}` : "",
          }),
        );

        return response;
      } catch (error) {
        console.error("Error fetching IM documents:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1, limit: 10 },
          },
        };
      }
    },
    [dispatch],
  );

  const handleBack = () => {
    const routeMapping = {
      [DocumentType.IM]: "/document/im-document",
      [DocumentType.TS]: "/document/terms-sheet",
    };
    navigate(`${routeMapping[type as DocumentType]}`);
  };

  return (
    <>
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-6 tw-pt-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                onClick={handleBack}
                className="tw-cursor-pointer"
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Change History
              </h1>
            </div>
          </div>

          <div className="tw-mt-4 tw-border tw-border-[#E9EAEB]">
            <CustomTable
              columns={columns}
              fetchData={fetchDocumentsWrapper}
              forceRefresh={forceRefresh}
              setForceRefresh={setForceRefresh}
              forceReset={forceReset}
              setForceReset={setForceReset}
              noDataMessage="No history found"
              noSearchDataMessage="No matching history found"
            />
          </div>
        </div>
      </div>

      {showPreview && (
        <FilePreviewModal
          document={documents}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};

export default IMDocumentHistoryTable;
